import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Screen } from "../components/ui/editorial/Screen.js";
import { Topline } from "../components/ui/editorial/Topline.js";
import { Portrait } from "../components/ui/editorial/Portrait.js";
import { useCurator } from "../useCurator.js";
import { trpc } from "../trpc.js";

const REPLY_MIN = 8;
const REPLY_MAX = 1200;

/**
 * Письмо от куратора. Данные — строго с бекенда (trpc.letters.get).
 * Никаких захардкоженных Ани/Марины/Лизы: если письма нет или API недоступен,
 * показываем честный empty state «куратор ещё не прислал».
 */
export function Letter(): JSX.Element {
  const navigate = useNavigate();
  const curator = useCurator();
  const params = useParams<{ id: string }>();
  const letterId = params.id ?? "";

  const isValidId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    letterId
  );

  const q = trpc.letters.get.useQuery(
    { letterId },
    {
      enabled: isValidId,
      retry: false,
      staleTime: 30_000,
    }
  );

  // Помечаем «видел письмо» локально (для Home persistence в fallback-режиме).
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem("companion:seen-letter", "1");
    } catch {
      /* privacy mode */
    }
  }, []);

  if (!isValidId) {
    return <EmptyState navigate={navigate} title="Адрес письма не распознан" />;
  }

  if (q.isLoading) {
    return (
      <Screen tone="paper">
        <div className="px-[22px] pt-[48px]">
          <Topline left="COMPANION" right="письмо · загружается" />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="editorial-meta text-ink/45">редактор отправляет письмо…</div>
        </div>
      </Screen>
    );
  }

  if (q.isError || !q.data) {
    return (
      <EmptyState
        navigate={navigate}
        title="Письмо ещё не готово"
        hint={`${curator.firstName} прочитает ваши ответы и пришлёт первое письмо. Уведомление придёт в Telegram.`}
      />
    );
  }

  const letter = q.data;
  const subjectName = letter.subject?.firstName ?? "собеседник";
  const body = letter.body;
  const paragraphs = body.split(/\n\n+/).filter(Boolean);
  const first = paragraphs[0] ?? "";
  const rest = paragraphs.slice(1);
  const dropcap = first.charAt(0) || "—";
  const firstWithoutDropcap = first.slice(1);

  return (
    <Screen tone="paper">
      <div className="px-[22px] pt-[48px]">
        <Topline left="COMPANION" right={`письмо · ${letterId.slice(0, 6)}`} />
      </div>

      <div className="flex flex-1 flex-col px-[22px] pt-[30px]">
        <div className="flex items-baseline justify-between editorial-meta text-ink/55">
          <span>{formatDate(letter.sentAt ?? letter.readAt)}</span>
          <span>{letter.status === "read" ? "прочитано" : letter.status}</span>
        </div>
        <div className="mt-[12px] h-px bg-ink/14" />

        <h1 className="mt-[30px] font-display font-light leading-[0.98] tracking-[-0.028em] text-ink text-[44px]">
          <em className="italic text-velvet">здравствуйте.</em>
        </h1>

        <div className="mt-[22px] flex items-center gap-[14px]">
          <div className="w-[78px] shrink-0">
            <Portrait seed={subjectName} aspect="4 / 5" className="rounded-[10px]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="editorial-kicker text-ink/55">портрет</div>
            <div className="mt-[4px] font-display italic font-light text-[18px] leading-[1.15] text-velvet truncate">
              {subjectName}
              {letter.subject?.age != null ? `, ${String(letter.subject.age)}` : ""}
            </div>
            {letter.subject?.city && (
              <div className="mt-[2px] editorial-meta text-ink/40">{letter.subject.city}</div>
            )}
          </div>
        </div>

        <div className="mt-[26px] font-display font-light text-[17px] leading-[1.5] text-ink/85 space-y-[14px]">
          <p>
            <span
              className="float-left font-display italic font-normal text-accent mr-[8px] mt-[6px]"
              style={{
                fontSize: "62px",
                lineHeight: "0.85",
                letterSpacing: "-0.02em",
              }}
            >
              {dropcap}
            </span>
            {firstWithoutDropcap}
          </p>
          {rest.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>

        <div className="mt-[28px] font-display italic font-light text-[15px] leading-[1.4] text-ink/85">
          {curator.letterSig}
          <small className="mt-[4px] block not-italic font-sans editorial-meta text-ink/50">
            {curator.letterRole}
          </small>
        </div>

        <div className="mt-[24px] flex items-center gap-[10px]">
          <span className="h-px flex-1 bg-ink/14" />
          <span aria-hidden className="inline-block h-[6px] w-[6px] rounded-full bg-accent" />
          <span className="h-px flex-1 bg-ink/14" />
        </div>
      </div>

      <ReplySection letterId={letter.id} status={letter.status} existingReply={letter.reply} />

      <div className="px-[22px] pb-[24px] pt-[20px]">
        <button
          type="button"
          onClick={() => {
            navigate("/home");
          }}
          className="editorial-meta text-ink/55 hover:text-ink transition-colors"
        >
          ← в редакцию
        </button>
      </div>
    </Screen>
  );
}

/**
 * Секция ответа. Три состояния:
 * 1. sent/read — textarea + кнопка «отправить», вызывает trpc.letters.reply
 * 2. replied  — показываем сохранённый текст ответа курсивом
 * 3. прочее   — пусто (skipped, drafted и т.п. до Letter вообще не доходят)
 */
function ReplySection({
  letterId,
  status,
  existingReply,
}: {
  letterId: string;
  status: string;
  existingReply: string | null;
}): JSX.Element | null {
  const curator = useCurator();
  const utils = trpc.useUtils();
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const replyMut = trpc.letters.reply.useMutation({
    onSuccess: () => {
      void utils.letters.get.invalidate({ letterId });
      void utils.letters.list.invalidate();
    },
    onError: (e) => {
      setError(e.message);
    },
  });

  if (status === "replied" && existingReply) {
    return (
      <div className="px-[22px] pt-[8px]">
        <div className="mt-[12px] flex items-center gap-[12px] editorial-meta text-ink/55">
          <span className="h-px flex-1 bg-ink/14" />
          <span>ваш ответ</span>
          <span className="h-px flex-1 bg-ink/14" />
        </div>
        <blockquote className="mt-[14px] pl-[14px] border-l-2 border-velvet/55 font-display italic font-light text-[16px] leading-[1.55] text-ink/85">
          {existingReply}
        </blockquote>
      </div>
    );
  }

  if (status !== "sent" && status !== "read") return null;

  const trimmed = body.trim();
  const canSend = trimmed.length >= REPLY_MIN && trimmed.length <= REPLY_MAX;
  const counter = `${String(trimmed.length)} / ${String(REPLY_MAX)}`;

  const onSend = (): void => {
    if (!canSend || replyMut.isPending) return;
    setError(null);
    replyMut.mutate({ letterId, body: trimmed });
  };

  return (
    <div className="px-[22px] pt-[8px]">
      <div className="mt-[12px] flex items-center gap-[12px] editorial-meta text-ink/55">
        <span className="h-px flex-1 bg-ink/14" />
        <span>ответить {curator.footerLabel}</span>
        <span className="h-px flex-1 bg-ink/14" />
      </div>

      <div className="mt-[14px] border-t border-b border-ink/14 py-[12px]">
        <textarea
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
          }}
          placeholder="что хотите сказать редактору — можно коротко…"
          rows={5}
          maxLength={REPLY_MAX}
          className="w-full resize-none bg-transparent font-display font-light text-[16px] leading-[1.5] text-ink placeholder:text-ink/35 outline-none"
        />
        <div className="mt-[8px] flex items-center justify-between">
          <span className="editorial-meta text-ink/40">
            {trimmed.length < REPLY_MIN ? `минимум ${String(REPLY_MIN)} символов` : counter}
          </span>
          <button
            type="button"
            disabled={!canSend || replyMut.isPending}
            onClick={onSend}
            className="font-display italic font-light text-[16px] tracking-[-0.005em] text-ink transition-opacity disabled:opacity-35 enabled:hover:opacity-75"
          >
            {replyMut.isPending ? "отправляем…" : "отправить"}{" "}
            <span className="text-accent">→</span>
          </button>
        </div>
      </div>

      {error && (
        <p className="mt-[10px] editorial-meta text-accent/85">Не получилось отправить: {error}</p>
      )}
    </div>
  );
}

function EmptyState({
  navigate,
  title,
  hint,
}: {
  navigate: (to: string) => void;
  title: string;
  hint?: string;
}): JSX.Element {
  return (
    <Screen tone="paper">
      <div className="px-[22px] pt-[48px]">
        <Topline left="COMPANION" right="редакция" />
      </div>
      <div className="flex flex-1 flex-col items-center justify-center px-[22px] text-center">
        <h1 className="font-display font-light text-[36px] leading-[1.05] text-ink">
          <em className="italic text-velvet">{title}</em>
        </h1>
        {hint && (
          <p className="mt-[16px] max-w-[320px] font-display italic font-light text-[15px] leading-[1.5] text-ink/65">
            {hint}
          </p>
        )}
      </div>
      <div className="px-[22px] pb-[24px]">
        <button
          type="button"
          onClick={() => {
            navigate("/home");
          }}
          className="editorial-meta text-ink/55 hover:text-ink transition-colors"
        >
          ← в редакцию
        </button>
      </div>
    </Screen>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const days = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${days[d.getDay()] ?? ""} · ${dd}.${mm}`;
}
